/**
 * Unified Loading System - Loading Context
 *
 * Provides global loading state management to prevent duplicate loaders.
 *
 * Key features:
 * - Only one loader active at a time
 * - Priority-based conflict resolution
 * - Registration/unregistration of loaders
 * - Source tracking for debugging
 */

import { createContext, useContext, useState, useEffect } from 'react';
import type { LoadingContextValue } from '../types';
import { LoaderPriority } from '../types';

/**
 * Default context value
 */
const DEFAULT_CONTEXT: LoadingContextValue = {
  globalLoading: false,
  activeSource: null,
  activePriority: Infinity,
  registerLoader: () => {
    throw new Error('LoadingContext used outside provider');
  },
  unregisterLoader: () => {
    throw new Error('LoadingContext used outside provider');
  },
  setGlobalLoading: () => false,
  canShowLoader: () => false,
};

/**
 * Loading Context
 */
export const LoadingContext = createContext<LoadingContextValue>(DEFAULT_CONTEXT);

/**
 * Hook to access loading context
 *
 * @example
 * ```tsx
 * const { globalLoading, activeSource, setGlobalLoading, canShowLoader } = useLoadingContext();
 *
 * // Set loading state
 * setGlobalLoading(true, 'my-component', LoaderPriority.PROGRESS);
 *
 * // Check if loader can be shown
 * if (canShowLoader('my-component', LoaderPriority.PROGRESS)) {
 *   // Show loader
 * }
 * ```
 */
export function useLoadingContext(): LoadingContextValue {
  const context = useContext(LoadingContext);

  if (!context || context === DEFAULT_CONTEXT) {
    console.warn('[useLoadingContext] LoadingContext used outside provider. Wrap your app with LoadingProvider.');
  }

  return context;
}

/**
 * Hook to check if global loading is active
 */
export function useGlobalLoading(): boolean {
  const { globalLoading } = useLoadingContext();
  return globalLoading;
}

/**
 * Hook to register a loader and get its unique ID
 */
export function useLoaderRegistration(
  source: string,
  priority: number = LoaderPriority.INLINE
): {
  isRegistered: boolean;
  canShow: boolean;
  setLoading: (loading: boolean) => boolean;
} {
  const context = useLoadingContext();
  const [isRegistered, setIsRegistered] = useState(false);

  // Register on mount, unregister on unmount
  useEffect(() => {
    if (!isRegistered) {
      context.registerLoader(source, priority);
      setIsRegistered(true);
    }

    return () => {
      if (isRegistered) {
        context.unregisterLoader(source);
        setIsRegistered(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isRegistered,
    canShow: context.canShowLoader(source, priority),
    setLoading: (loading: boolean) => context.setGlobalLoading(loading, source, priority),
  };
}

export default LoadingContext;
